import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LayoutSettings } from "../../../../boxes/LayoutSettings/LayoutSettings";
import { EntityHeader } from "../../../../widgets/EntityHeader/EntityHeader";
import { Box } from "../../../../shared/Box/Box";
import { http } from "../../../../services/HttpService";
import { HTTP_ENDPOINTS } from "../../../../consts/api";
import { uiNotification } from "../../../../services/UINotificationService";
import styles from "./wellsync.module.scss";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";

const emptyForm = () => ({
  clinicId: "",
  wellsyncClinicId: "",
  clinicApiKey: "",
  signingKey: "",
  pharmacyPrivateKeyPem: "",
  isEnabled: true,
});

function sameClinicId(a, b) {
  return String(a) === String(b);
}

function getClinicDisplayName(clinicId, clinicsList) {
  const row = clinicsList.find((c) => sameClinicId(c.id, clinicId));
  if (!row) return "";
  const name = (row.clinic_name || row.name || "").trim();
  return name || `Clinic ${row.id}`;
}

export function WellSyncLegacyConfig({ onBack }) {
  const [configs, setConfigs] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, clinRes] = await Promise.all([
        http.get(HTTP_ENDPOINTS.wellsync.configs()),
        http.get(HTTP_ENDPOINTS.getClinics()),
      ]);
      const cfgData = cfgRes.data && cfgRes.data.data;
      setConfigs(Array.isArray(cfgData) ? cfgData : []);
      const raw = clinRes.data && clinRes.data.data;
      let list;
      if (Array.isArray(raw)) {
        list = raw;
      } else {
        const inner = raw && raw.data;
        list = inner != null ? inner : raw != null ? raw : [];
      }
      setClinics(Array.isArray(list) ? list : []);
    } catch (e) {
      uiNotification.error("Failed to load WellSync settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const clinicOptions = useMemo(() => {
    const configured = new Set(configs.map((c) => Number(c.clinicId)));
    return clinics.filter((c) => c.id != null && !configured.has(Number(c.id)));
  }, [clinics, configs]);

  const editingConfig = useMemo(
    () => configs.find((x) => String(x.id) === String(editingId)) || null,
    [configs, editingId],
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.clinicId || !form.wellsyncClinicId) {
      uiNotification.error("Clinic and WellSync clinic id are required");
      return;
    }
    setSaving(true);
    try {
      await http.post(HTTP_ENDPOINTS.wellsync.configs(), {
        clinicId: Number(form.clinicId),
        wellsyncClinicId: form.wellsyncClinicId.trim(),
        clinicApiKey: form.clinicApiKey.trim(),
        signingKey: form.signingKey.trim(),
        pharmacyPrivateKeyPem: form.pharmacyPrivateKeyPem.trim() || undefined,
        isEnabled: form.isEnabled,
      });
      uiNotification.success("WellSync configuration saved");
      setForm(emptyForm());
      await load();
    } catch (err) {
      uiNotification.error(
        err.response?.data?.message || "Failed to save configuration",
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({
      clinicId: c.clinicId,
      wellsyncClinicId: c.wellsyncClinicId,
      clinicApiKey: "",
      signingKey: "",
      pharmacyPrivateKeyPem: "",
      isEnabled: c.isEnabled,
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    setSaving(true);
    try {
      const payload = {
        wellsyncClinicId: editForm.wellsyncClinicId.trim(),
        isEnabled: editForm.isEnabled,
      };
      if (editForm.clinicApiKey.trim()) {
        payload.clinicApiKey = editForm.clinicApiKey.trim();
      }
      if (editForm.signingKey.trim()) {
        payload.signingKey = editForm.signingKey.trim();
      }
      if (editForm.pharmacyPrivateKeyPem.trim()) {
        payload.pharmacyPrivateKeyPem = editForm.pharmacyPrivateKeyPem.trim();
      }
      await http.put(`wellsync/configs/${editingId}`, payload);
      uiNotification.success("WellSync configuration updated");
      setEditingId(null);
      await load();
    } catch (err) {
      uiNotification.error(
        err.response?.data?.message || "Failed to update configuration",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove WellSync configuration for this clinic?")) {
      return;
    }
    setSaving(true);
    try {
      await http.delete(`wellsync/configs/${id}`);
      uiNotification.success("Configuration removed");
      await load();
    } catch (err) {
      uiNotification.error("Failed to delete configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (c) => {
    const nextEnabled = !c.isEnabled;
    const clinicLabel =
      getClinicDisplayName(c.clinicId, clinics) || `Clinic ${c.clinicId}`;
    const confirmMessage = nextEnabled
      ? `Enable WellSync for "${clinicLabel}"?`
      : `Disable WellSync for "${clinicLabel}"?`;
    if (!window.confirm(confirmMessage)) return;
    setSaving(true);
    try {
      await http.post(`wellsync/configs/${c.id}/toggle`, {
        isEnabled: nextEnabled,
      });
      await load();
    } catch (err) {
      uiNotification.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <LayoutSettings contentClassName={styles.root}>
      <EntityHeader title="WellSync — Manual credentials" />
      <Box>
        <button type="button" className={styles.btnGhost} onClick={onBack}>
          ← Back to integration wizard
        </button>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <p>
              Paste clinic credentials from the WellSync portal. Keys are stored
              encrypted server-side and never returned to the browser.
            </p>
            <div className={styles.card}>
              <h3>Add clinic</h3>
              <form onSubmit={handleCreate}>
                <div className={styles.field}>
                  <label htmlFor="clinicId">Clinic</label>
                  <select
                    id="clinicId"
                    className={styles.input}
                    value={form.clinicId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, clinicId: e.target.value }))
                    }
                    required
                  >
                    <option value="">Select clinic</option>
                    {clinicOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.clinic_name || c.name || c.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label htmlFor="wellsyncClinicId">WellSync clinic id</label>
                  <input
                    id="wellsyncClinicId"
                    className={styles.input}
                    value={form.wellsyncClinicId}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        wellsyncClinicId: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="clinicApiKey">Clinic API key</label>
                  <input
                    id="clinicApiKey"
                    className={styles.input}
                    type="password"
                    value={form.clinicApiKey}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, clinicApiKey: e.target.value }))
                    }
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="signingKey">Signing key</label>
                  <input
                    id="signingKey"
                    className={styles.input}
                    type="password"
                    value={form.signingKey}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, signingKey: e.target.value }))
                    }
                    required
                  />
                </div>
                <button
                  type="submit"
                  className={styles.btnPrimary}
                  disabled={saving}
                >
                  Save
                </button>
              </form>
            </div>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Clinic</th>
                  <th>WellSync id</th>
                  <th>Enabled</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {configs.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {getClinicDisplayName(c.clinicId, clinics) || c.clinicId}
                    </td>
                    <td>{c.wellsyncClinicId}</td>
                    <td>{c.isEnabled ? "Yes" : "No"}</td>
                    <td className={styles.actions}>
                      <button
                        type="button"
                        className={styles.btnGhost}
                        onClick={() => void handleToggle(c)}
                      >
                        Toggle
                      </button>
                      <button
                        type="button"
                        className={styles.btnGhost}
                        onClick={() => startEdit(c)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={styles.btnDanger}
                        onClick={() => void handleDelete(c.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {editingId && editingConfig ? (
              <div className={styles.card}>
                <form onSubmit={handleUpdate}>
                  <div className={styles.field}>
                    <label htmlFor="editWellsyncClinicId">
                      WellSync clinic id
                    </label>
                    <input
                      id="editWellsyncClinicId"
                      className={styles.input}
                      value={editForm.wellsyncClinicId}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          wellsyncClinicId: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="editClinicApiKey">
                      Clinic API key (optional replace)
                    </label>
                    <input
                      id="editClinicApiKey"
                      className={styles.input}
                      type="password"
                      value={editForm.clinicApiKey}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          clinicApiKey: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <button type="submit" className={styles.btnPrimary}>
                    Update
                  </button>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            ) : null}
          </>
        )}
      </Box>
    </LayoutSettings>
  );
}
